import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Event, MyEventRegistration, RegistrationPaymentStatus } from '../../types';
import {
  MapPin,
  Calendar as CalendarIcon,
  Users,
  ExternalLink,
  Sparkles,
  UserRound,
  Clock,
  Ticket,
  X,
} from 'lucide-react';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { getApiBaseUrl } from '../../lib/api-base';
import { resolveEventImageSrc } from '../../lib/event-flyer';
import {
  formatRupees,
  groupEventsByStatus,
  memberVisibleEvents,
  MY_PAYMENT_STATUS_LABELS,
  eventSkipsRegistration,
  presentText,
  eventRegistrationOpeningSoon,
  registrationStatusLabel,
} from '../../lib/event-utils';
import { CANCELLED_PAYMENT_MEMBER_NOTE } from '../../lib/payment-status';
import { cn } from '../../lib/utils';
import { EVENT_SCORES_PATH, hasEventScoresLink } from '../../lib/event-scores-link';
import { MarkdownContent } from '../markdown/MarkdownContent';
import { VenueName } from './VenueName';
import {
  EventCollectionButtons,
  EventDropPointsDialog,
  EventPickupRequestDialog,
} from './EventCollectionDialogs';

const PAYMENT_STATUS_TONE: Record<RegistrationPaymentStatus, string> = {
  not_required: 'bg-[var(--color-success-subtle)] text-[var(--color-success-text)]',
  verified: 'bg-[var(--color-success-subtle)] text-[var(--color-success-text)]',
  pending: 'bg-[var(--color-warning-subtle)] text-[var(--color-text-primary)]',
  rejected: 'bg-[var(--color-error-subtle)] text-[var(--color-error-text)]',
};

function MyRegistrationBadge({ registration }: { registration: MyEventRegistration }) {
  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5 min-w-0">
        <Ticket className="w-4 h-4 text-[var(--color-accent)] shrink-0" />
        <div className="min-w-0">
          <p className="text-xs text-[var(--color-text-tertiary)]">Your registration</p>
          <p className="text-sm font-semibold font-mono text-[var(--color-text-primary)]">{registration.regNumber}</p>
        </div>
      </div>
      <div className="text-right shrink-0">
        <span
          className={cn(
            'inline-block text-xs font-medium px-2 py-1 rounded-[var(--radius-sm)]',
            PAYMENT_STATUS_TONE[registration.paymentStatus],
          )}
        >
          {MY_PAYMENT_STATUS_LABELS[registration.paymentStatus]}
        </span>
        {registration.amountDue != null && (
          <p className="text-xs text-[var(--color-text-tertiary)] mt-1">{formatRupees(registration.amountDue)}</p>
        )}
      </div>
    </div>
  );
}

function RegistrationAction({
  event,
  onQuickRegister,
}: {
  event: Event;
  onQuickRegister?: (event: Event) => void;
}) {
  if (event.myRegistration) {
    return (
      <div className="space-y-2">
        <MyRegistrationBadge registration={event.myRegistration} />
        {event.myRegistration.paymentStatus === 'rejected' && (
          <p className="text-xs text-[var(--color-text-secondary)]">
            {CANCELLED_PAYMENT_MEMBER_NOTE}
          </p>
        )}
      </div>
    );
  }

  if (eventSkipsRegistration(event)) {
    return (
      <p className="text-center text-sm font-medium text-[var(--color-text-secondary)] py-2">
        No registration needed
      </p>
    );
  }

  if (eventRegistrationOpeningSoon(event)) {
    return (
      <p className="w-full rounded-[var(--radius-md)] bg-[var(--color-surface-2)] py-3 text-center text-sm font-medium text-[var(--color-text-secondary)]">
        {registrationStatusLabel(false, 'coming_soon')}
      </p>
    );
  }

  if (event.registrationOpen && onQuickRegister) {
    const fee = registrationFeeLabel(event);
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-[var(--color-text-secondary)]">
          {fee && <span>{fee}</span>}
          <span className="flex items-center gap-1">
            {event.deadlineAt && (
              <>
                <Clock className="w-3.5 h-3.5" />
                Closes in {formatDistanceToNowStrict(new Date(event.deadlineAt))}
              </>
            )}
            {event.deadlineAt && event.remaining != null && ' · '}
            {event.remaining != null && `${event.remaining} spots left`}
          </span>
        </div>
        <button
          type="button"
          onClick={() => onQuickRegister(event)}
          className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-[var(--radius-md)] font-medium transition-colors duration-[var(--duration-fast)] bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]"
        >
          Quick register
        </button>
      </div>
    );
  }

  if (event.registrationClosedReason && event.registrationClosedReason !== 'closed') {
    return (
      <p className="text-center text-sm text-[var(--color-text-tertiary)] py-2">
        {registrationStatusLabel(false, event.registrationClosedReason)}
      </p>
    );
  }

  return null;
}

function eventImageSrc(image: string | null): string | null {
  return resolveEventImageSrc(image, getApiBaseUrl());
}

function registrationFeeLabel(event: Event): string | null {
  const options = event.formOptions;
  if (!options.paymentRequired) return 'Free';
  const parts: string[] = [];
  if (options.memberChoice && event.feeMember != null) parts.push(`${formatRupees(event.feeMember)} members`);
  if (event.feeNonMember != null) {
    parts.push(options.memberChoice ? `${formatRupees(event.feeNonMember)} others` : formatRupees(event.feeNonMember));
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

const detailsButtonClass =
  'inline-flex items-center justify-center gap-2 w-full py-3 rounded-[var(--radius-md)] font-medium border border-[var(--color-border)] text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)] transition-colors duration-[var(--duration-fast)]';

const accentButtonClass =
  'inline-flex items-center justify-center gap-2 w-full py-3 rounded-[var(--radius-md)] font-medium transition-colors duration-[var(--duration-fast)] bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]';

function JoinWhatsappButton({
  url,
  prominent,
  className,
}: {
  url: string | null | undefined;
  prominent?: boolean;
  className?: string;
}) {
  const href = url?.trim();
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(prominent ? accentButtonClass : detailsButtonClass, className)}
    >
      Join WhatsApp group
      <ExternalLink className="w-4 h-4" />
    </a>
  );
}

function EventCtaButton({ event }: { event: Event }) {
  if (event.ctaMode === 'none' || !event.ctaLabel || !event.ctaUrl) return null;

  const className = accentButtonClass;

  if (event.ctaMode === 'internal') {
    return (
      <Link to={event.ctaUrl} className={className}>
        {event.ctaLabel}
      </Link>
    );
  }

  return (
    <a href={event.ctaUrl} target="_blank" rel="noopener noreferrer" className={className}>
      {event.ctaLabel}
      <ExternalLink className="w-4 h-4" />
    </a>
  );
}

function ViewDetailsButton({ event, onClick }: { event: Event; onClick: () => void }) {
  if (!eventImageSrc(event.image)) return null;
  return (
    <button type="button" onClick={onClick} className={detailsButtonClass}>
      View Details
    </button>
  );
}

export function EventDetailsDialog({
  event,
  onClose,
  onDropPoints,
  onPickup,
  suspendDismiss = false,
}: {
  event: Event;
  onClose: () => void;
  onDropPoints?: () => void;
  onPickup?: () => void;
  suspendDismiss?: boolean;
}) {
  const imageSrc = eventImageSrc(event.image);

  useEffect(() => {
    if (!imageSrc || suspendDismiss) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [imageSrc, onClose, suspendDismiss]);

  if (!imageSrc) return null;

  const openingSoon = eventRegistrationOpeningSoon(event);
  const skipsRegistration = eventSkipsRegistration(event);
  const scrollsWithNote = Boolean(event.whatsappGroupUrl?.trim()) || skipsRegistration || openingSoon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[oklch(18%_0.01_55/0.72)] p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Event flyer"
      onClick={() => {
        if (!suspendDismiss) onClose();
      }}
    >
      <div className="relative w-full max-w-3xl" onClick={(ev) => ev.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-12 right-0 rounded-full bg-[var(--color-surface-1)] p-2 text-[var(--color-text-primary)] shadow-[var(--shadow-md)]"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        <div
          className={scrollsWithNote ? 'max-h-[calc(100vh-6rem)] overflow-y-auto' : undefined}
        >
          <img
            src={imageSrc}
            alt={event.title}
            className={cn(
              'h-auto w-full rounded-[var(--radius-lg)] object-contain shadow-[var(--shadow-xl)]',
              !scrollsWithNote && 'max-h-[calc(100vh-6rem)]',
            )}
          />
          {skipsRegistration && (
            <p className="mt-3 text-center text-sm font-medium text-[var(--color-text-secondary)]">
              No registration needed
            </p>
          )}
          {openingSoon && (
            <p className="mt-3 w-full rounded-[var(--radius-md)] bg-[var(--color-surface-2)] py-3 text-center text-sm font-medium text-[var(--color-text-secondary)]">
              {registrationStatusLabel(false, 'coming_soon')}
            </p>
          )}
          {onDropPoints && onPickup && (
            <div className="mt-3 space-y-3">
              <EventCollectionButtons event={event} onDropPoints={onDropPoints} onPickup={onPickup} />
            </div>
          )}
          <JoinWhatsappButton url={event.whatsappGroupUrl} prominent className="mt-3" />
        </div>
      </div>
    </div>
  );
}

function LiveEventCard({
  event,
  badge,
  onViewDetails,
  onQuickRegister,
  onDropPoints,
  onPickup,
}: {
  event: Event;
  badge: string;
  onViewDetails: () => void;
  onQuickRegister?: (event: Event) => void;
  onDropPoints: () => void;
  onPickup: () => void;
}) {
  const eventDate = new Date(event.date);
  const description = presentText(event.description);
  const location = presentText(event.location);

  return (
    <div className="bg-[var(--color-surface-1)] rounded-[var(--radius-lg)] border border-[var(--color-border)] hover:shadow-[var(--shadow-md)] transition-shadow duration-[var(--duration-normal)] flex flex-col p-6">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">{event.title}</h3>
        <span className="shrink-0 text-xs font-medium px-2 py-1 rounded-[var(--radius-sm)] bg-[var(--color-surface-2)] text-[var(--color-text-secondary)]">
          {badge}
        </span>
      </div>
      {description && (
        <MarkdownContent
          text={description}
          className="text-[var(--color-text-secondary)] text-sm mt-1"
        />
      )}

      <div className="space-y-2 mt-4 mb-6">
        <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <CalendarIcon className="w-4 h-4 text-[var(--color-text-tertiary)] shrink-0" />
          {format(eventDate, 'PPP')} at {format(eventDate, 'p')}
        </div>
        {location && (
          <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
            <MapPin className="w-4 h-4 text-[var(--color-text-tertiary)] shrink-0" />
            <VenueName name={location} url={event.venueMapUrl} />
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <Users className="w-4 h-4 text-[var(--color-text-tertiary)] shrink-0" />
          {event.participantCount} participating
        </div>
        {event.isParticipating && (
          <p className="text-xs font-medium text-[var(--color-success-text)]">You are on the roster</p>
        )}
      </div>

      <div className="mt-auto space-y-3">
        <EventCollectionButtons event={event} onDropPoints={onDropPoints} onPickup={onPickup} />
        <ViewDetailsButton event={event} onClick={onViewDetails} />
        <JoinWhatsappButton
          url={event.whatsappGroupUrl}
          prominent={!event.registrationOpen || Boolean(event.myRegistration)}
        />
        <RegistrationAction event={event} onQuickRegister={onQuickRegister} />
        {!(event.registrationOpen && onQuickRegister && !event.myRegistration) && (
          <EventCtaButton event={event} />
        )}
      </div>
    </div>
  );
}

function PastEventCard({
  event,
  onViewDetails,
  onDropPoints,
  onPickup,
}: {
  event: Event;
  onViewDetails: () => void;
  onDropPoints: () => void;
  onPickup: () => void;
}) {
  const eventDate = new Date(event.date);
  const description = presentText(event.description);
  const coordinator = presentText(event.coordinator);
  const specialMention = presentText(event.specialMention);

  return (
    <div className="bg-[var(--color-surface-1)] rounded-[var(--radius-lg)] border border-[var(--color-border)] p-5 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-[var(--color-text-primary)]">{event.title}</h3>
          <p className="text-xs text-[var(--color-text-tertiary)] mt-1">{format(eventDate, 'PPP')}</p>
        </div>
        <span className="text-xs font-medium px-2 py-1 rounded-[var(--radius-sm)] bg-[var(--color-surface-2)] text-[var(--color-text-secondary)]">
          Past
        </span>
      </div>

      {description && (
        <MarkdownContent text={description} className="text-sm text-[var(--color-text-secondary)]" />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
        {coordinator && (
          <div className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]">
            <UserRound className="w-4 h-4 text-[var(--color-text-tertiary)] shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-[var(--color-text-tertiary)]">Coordinator</p>
              <p>{coordinator}</p>
            </div>
          </div>
        )}
        <div className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]">
          <Users className="w-4 h-4 text-[var(--color-text-tertiary)] shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-[var(--color-text-tertiary)]">Participants</p>
            <p>{event.participantCount}</p>
          </div>
        </div>
      </div>

      {specialMention && (
        <div className="flex items-start gap-2 rounded-[var(--radius-md)] bg-[var(--color-surface-2)] p-3">
          <Sparkles className="w-4 h-4 text-[var(--color-accent)] shrink-0 mt-0.5" />
          <MarkdownContent text={specialMention} className="text-sm text-[var(--color-text-secondary)] min-w-0" />
        </div>
      )}

      {event.myRegistration && <MyRegistrationBadge registration={event.myRegistration} />}
      {eventSkipsRegistration(event) && (
        <p className="text-sm font-medium text-[var(--color-text-secondary)]">No registration needed</p>
      )}
      <EventCollectionButtons event={event} onDropPoints={onDropPoints} onPickup={onPickup} />
      <JoinWhatsappButton url={event.whatsappGroupUrl} />
      {hasEventScoresLink(event) ? <Link to={EVENT_SCORES_PATH} className={accentButtonClass}>View scores</Link> : null}
      <ViewDetailsButton event={event} onClick={onViewDetails} />
    </div>
  );
}

function EventSection({
  title,
  description,
  events,
  variant,
  onViewDetails,
  onQuickRegister,
  onDropPoints,
  onPickup,
}: {
  title: string;
  description?: string;
  events: Event[];
  variant: 'live' | 'past';
  onViewDetails: (event: Event) => void;
  onQuickRegister?: (event: Event) => void;
  onDropPoints: (event: Event) => void;
  onPickup: (event: Event) => void;
}) {
  if (events.length === 0) return null;

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">{title}</h2>
        {description && (
          <p className="text-sm text-[var(--color-text-secondary)] mt-1">{description}</p>
        )}
      </div>

      {variant === 'live' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {events.map((event) => (
            <div key={event.id}>
              <LiveEventCard
                event={event}
                badge={event.status === 'active' ? 'Active' : 'Upcoming'}
                onViewDetails={() => onViewDetails(event)}
                onQuickRegister={onQuickRegister}
                onDropPoints={() => onDropPoints(event)}
                onPickup={() => onPickup(event)}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((event) => (
            <div key={event.id}>
              <PastEventCard
                event={event}
                onViewDetails={() => onViewDetails(event)}
                onDropPoints={() => onDropPoints(event)}
                onPickup={() => onPickup(event)}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function EventsPageContent({
  events,
  loading,
  onQuickRegister,
}: {
  events: Event[];
  loading: boolean;
  onQuickRegister?: (event: Event) => void;
}) {
  const { active, upcoming, past } = groupEventsByStatus(memberVisibleEvents(events));
  const hasAny = active.length + upcoming.length + past.length > 0;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dropId, setDropId] = useState<string | null>(null);
  const [pickupId, setPickupId] = useState<string | null>(null);
  const selected = selectedId ? (events.find((event) => event.id === selectedId) ?? null) : null;
  const dropEvent = dropId ? (events.find((event) => event.id === dropId) ?? null) : null;
  const pickupEvent = pickupId ? (events.find((event) => event.id === pickupId) ?? null) : null;

  if (loading && events.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="h-64 bg-[var(--color-surface-2)] rounded-[var(--radius-lg)] animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!hasAny) {
    return (
      <div className="py-20 text-center bg-[var(--color-surface-1)] rounded-[var(--radius-xl)] border border-dashed border-[var(--color-border)]">
        <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-[var(--color-text-tertiary)]" />
        <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">No events yet</h2>
        <p className="text-[var(--color-text-secondary)] text-sm mt-2 max-w-md mx-auto">
          Community gatherings and activities will appear here when scheduled.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <EventSection
        title="Active Events"
        events={active}
        variant="live"
        onViewDetails={(event) => setSelectedId(event.id)}
        onQuickRegister={onQuickRegister}
        onDropPoints={(event) => setDropId(event.id)}
        onPickup={(event) => setPickupId(event.id)}
      />
      <EventSection
        title="Upcoming Events"
        events={upcoming}
        variant="live"
        onViewDetails={(event) => setSelectedId(event.id)}
        onQuickRegister={onQuickRegister}
        onDropPoints={(event) => setDropId(event.id)}
        onPickup={(event) => setPickupId(event.id)}
      />
      <EventSection
        title="Past Events"
        description="Showing events from the last 12 months."
        events={past}
        variant="past"
        onViewDetails={(event) => setSelectedId(event.id)}
        onDropPoints={(event) => setDropId(event.id)}
        onPickup={(event) => setPickupId(event.id)}
      />
      {selected && eventImageSrc(selected.image) ? (
        <EventDetailsDialog
          event={selected}
          onClose={() => setSelectedId(null)}
          onDropPoints={() => setDropId(selected.id)}
          onPickup={() => setPickupId(selected.id)}
          suspendDismiss={Boolean(dropEvent || pickupEvent)}
        />
      ) : null}
      {dropEvent ? <EventDropPointsDialog event={dropEvent} onClose={() => setDropId(null)} /> : null}
      {pickupEvent ? <EventPickupRequestDialog event={pickupEvent} onClose={() => setPickupId(null)} /> : null}
    </div>
  );
}
